import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigation } from '@coherence-design-system/navigation';
import { INavLink, INavLinkGroup } from '@coherence-design-system/controls';
import { Flights, HttpVerbs, UATConstants } from 'src/Common/Models/UATConstants';
import { useHistory } from 'react-router-dom';
import { UserContext } from 'src/Models/UserProfile';
import type { StoreState } from 'src/Store';
import { selectTenants, userHasRoleScope } from 'src/Common/Models/StoreConstants';
import { LocalTenantConfig } from 'src/tenantConfig';
import { useIsMobile, Viewport } from '@coherence-design-system/controls';
import { useSelector } from 'react-redux';
import { store as RtkStore } from 'src/Common/RTK/store/index';
import { setManageViewSelectedTenantId } from 'src/Common/manageViews/store/slices/viewSlice';
import { trackUserContextTenantAuth } from 'src/utils/helpers/ROBTelemetry';
import {
  ContextualMenu,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  DirectionalHint,
  Icon,
  IconButton,
  IContextualMenuProps,
  PrimaryButton,
  TeachingBubble,
} from '@fluentui/react';
import { NotificationsContext } from 'src/notifications';
import DOMPurify from 'dompurify';
import { ILegacyNotification } from 'src/notifications/models';
import useDismissNotification from 'src/notifications/hooks/useDismissNotification';
import { ServiceFactoryClientExecuteApi } from 'src/Common/Services/ServiceFactoryToExecuteApi';
import { showErrorToast, showSuccessToast } from 'src/Common/Utils/toastUtils';
import { appInsights } from 'src/Services/Insights';

const ALL_DEFAULT_MENU_KEY = 'All';
const DEFAULT_TENANT_TEACHING_BUBBLE_TAG = 'TeachingBubble:SetAsDefaultTenant';
const DEFAULT_TENANT_TOAST_SCOPE = 'default-tenant';

type DefaultTenantAction = 'Set' | 'Replace' | 'Remove';
type DefaultTenantTelemetryEvent =
  | 'MenuOpened'
  | 'ActionSelected'
  | 'ReplacementCancelled'
  | 'TeachingBubbleShown'
  | `${DefaultTenantAction}-Success`;

const trackDefaultTenantEvent = (
  event: DefaultTenantTelemetryEvent,
  properties: Record<string, string | boolean | null>
): void => {
  try {
    appInsights.trackEvent(
      { name: `UAT-LeftNavigation-DefaultTenant-${event}` },
      { feature: 'SetAsDefault', source: 'LeftNavigation', ...properties }
    );
  } catch (error) {
    console.error('Failed to track default tenant telemetry:', error);
  }
};

const getDefaultTenantId = (userSettings: { defaultTenant?: unknown } | null | undefined): string | null => {
  const defaultTenant = userSettings?.defaultTenant;
  if (typeof defaultTenant !== 'string') {
    return null;
  }

  const normalizedDefaultTenant = defaultTenant.trim();
  return normalizedDefaultTenant && normalizedDefaultTenant.toLowerCase() !== 'null'
    ? normalizedDefaultTenant
    : null;
};

const tenantIdsMatch = (firstTenantId: string | null, secondTenantId: string | null): boolean =>
  Boolean(
    firstTenantId &&
    secondTenantId &&
    firstTenantId.toLowerCase() === secondTenantId.toLowerCase()
  );

interface DefaultMenuButtonProps {
  isDefault: boolean;
  isHovered: boolean;
  menuName: string;
  menuProps: IContextualMenuProps;
  onMenuOpen: () => void;
  isTeachingBubbleVisible: boolean;
  onTeachingBubbleClose: () => void;
  onTeachingBubbleShown: () => void;
  teachingBubble?: ILegacyNotification;
  isTeachingBubbleDismissSubmitting: boolean;
  onTeachingBubbleDismiss: (notificationId: string) => Promise<boolean>;
}

const DefaultMenuButton: React.FC<DefaultMenuButtonProps> = ({
  isDefault,
  isHovered,
  menuName,
  menuProps,
  onMenuOpen,
  isTeachingBubbleVisible,
  onTeachingBubbleClose,
  onTeachingBubbleShown,
  teachingBubble,
  isTeachingBubbleDismissSubmitting,
  onTeachingBubbleDismiss,
}) => {
  const [menuButtonTarget, setMenuButtonTarget] = useState<HTMLElement | null>(null);
  const [menuTarget, setMenuTarget] = useState<HTMLElement | null>(null);
  const menuItemAriaLabel = `Set As Default for ${menuName}`;
  const accessibleMenuProps: IContextualMenuProps = {
    ...menuProps,
    items: menuProps.items.map((item, index) =>
      index === 0
        ? {
          ...item,
          ariaLabel: menuItemAriaLabel,
        }
        : item
    ),
  };

  const dismissMenu = () => {
    setMenuTarget(null);
  };

  useEffect(() => {
    if (menuButtonTarget && isTeachingBubbleVisible && teachingBubble) {
      onTeachingBubbleShown();
    }
  }, [menuButtonTarget, isTeachingBubbleVisible, teachingBubble, onTeachingBubbleShown]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isDefault && (
          <Icon
            aria-hidden={true}
            iconName='PinnedSolid'
            styles={{
              root: {
                color: '#0078d4',
                fontSize: 12,
                textAlign: 'center',
                width: 20,
              },
            }}
          />
        )}
        <IconButton
          elementRef={setMenuButtonTarget}
          className='default-menu-action'
          ariaLabel={`More options for ${menuName}`}
          aria-haspopup='menu'
          iconProps={{ iconName: 'MoreVertical' }}
          onClick={(event) => {
            event.stopPropagation();
            if (!menuTarget) {
              onMenuOpen();
            }
            onTeachingBubbleClose();
            setMenuTarget(event.currentTarget as HTMLElement);
          }}
          styles={{
            root: {
              height: 20,
              minWidth: 20,
              width: 20,
              opacity: isHovered || menuTarget || isTeachingBubbleVisible ? 1 : 0,
              pointerEvents:
                isHovered || menuTarget || isTeachingBubbleVisible ? 'auto' : 'none',
              backgroundColor: 'transparent',
            },
            rootHovered: {
              backgroundColor: '#edebe9',
            },
            rootFocused: {
              backgroundColor: '#edebe9',
            },
            rootPressed: {
              backgroundColor: '#e1dfdd',
            },
          }}
          title={`More options for ${menuName}`}
        />
      </div>
      {menuTarget && (
        <ContextualMenu
          {...accessibleMenuProps}
          target={menuTarget}
          onDismiss={dismissMenu}
          onRestoreFocus={({ originalElement, containsFocus }) => {
            // Restore synchronously so an outside click can focus the newly selected tenant.
            if (containsFocus) {
              originalElement?.focus();
            }
          }}
        />
      )}
      {menuButtonTarget && isTeachingBubbleVisible && teachingBubble && (
        <TeachingBubble
          target={menuButtonTarget}
          headline={teachingBubble.title}
          hasCloseButton={true}
          closeButtonAriaLabel='Close'
          isWide={false}
          primaryButtonProps={{
            children: 'Got It',
            disabled: isTeachingBubbleDismissSubmitting,
            onClick: async () => {
              const isDismissed = await onTeachingBubbleDismiss(`${teachingBubble.id}`);
              if (isDismissed) {
                onTeachingBubbleClose();
                setMenuTarget(null);
              }
            },
          }}
          onDismiss={onTeachingBubbleClose}
          styles={{
            content: {
              width: 340,
              maxWidth: 340,
              height: 'auto',
            },
            bodyContent: {
              boxSizing: 'border-box',
            },
            footer: {
              boxSizing: 'border-box',
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            },
            primaryButton: {
              marginLeft: 'auto',
              backgroundColor: '#ffffff',
              borderColor: '#ffffff',
              color: '#0078d4',
              selectors: {
                ':hover': {
                  backgroundColor: '#ffffff',
                  borderColor: '#ffffff',
                  color: '#005a9e',
                },
                ':focus': {
                  backgroundColor: '#ffffff',
                  borderColor: '#605e5c',
                  color: '#0078d4',
                },
                ':active': {
                  backgroundColor: '#ffffff',
                  borderColor: '#ffffff',
                  color: '#0078d4',
                },
                '.ms-Button-label': {
                  backgroundColor: 'transparent',
                },
              },
            },
          }}
          calloutProps={{
            directionalHint: DirectionalHint.rightCenter,
            gapSpace: 8,
            calloutWidth: 340,
            calloutMaxWidth: 340,
          }}>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(teachingBubble.htmlMessage, {
                ADD_ATTR: ['target'],
                ALLOWED_ATTR: ['href', 'target', 'class', 'id', 'style'],
              }),
            }}
          />
        </TeachingBubble>
      )}
    </>
  );
};

interface NavigationProps {
  user: UserContext;
  updateDefaultTenant: (defaultTenant: string | null) => Promise<void>;
  isNavCollapsed: boolean;
  isManageViewsOpen: boolean;
  handleManageViewClick: (data: boolean) => void;
}

interface DefaultReplacement {
  tenantId: string | null;
  menuName: string;
  currentMenuName: string;
}

export const LeftNavigation: React.FC<NavigationProps> = ({
  user,
  updateDefaultTenant,
  isNavCollapsed,
  isManageViewsOpen,
  handleManageViewClick,
}) => {
  const navigate = useHistory();
  const isInitialOverviewRoute =
    window.location.pathname === UATConstants.Routes.Overview ||
    window.location.pathname.toLowerCase().replace(/\s/g, '') === UATConstants.Routes.Home ||
    window.location.pathname === UATConstants.Routes.LandingPage;
  const {
    legacyNotifications,
    notificationConfig,
    setLegacyNotifications,
  } = React.useContext(NotificationsContext);
  const handleTeachingBubbleDismissed = useCallback((notificationIds: string[]) => {
    setLegacyNotifications(
      legacyNotifications.filter(
        (notification) => !notificationIds.includes(`${notification.id}`)
      )
    );
  }, [legacyNotifications, setLegacyNotifications]);
  const {
    dismiss: dismissTeachingBubble,
    isSubmitting: isTeachingBubbleDismissSubmitting,
  } = useDismissNotification({
    apiUrl: notificationConfig.dismissApiUrl,
    authToken: `Bearer ${notificationConfig.apiToken}`,
    coreApiToken: notificationConfig.coreApiToken,
    graphToken: notificationConfig.graphToken,
    userId: notificationConfig.userEmail || user.email,
    handleDismiss: handleTeachingBubbleDismissed,
  });
  const userDefaultTenantId = getDefaultTenantId(user.userSettings);
  const [defaultTenantId, setDefaultTenantId] = useState<string | null>(userDefaultTenantId);
  const [isDefaultTenantSubmitting, setIsDefaultTenantSubmitting] = useState(false);
  const [hoveredMenuKey, setHoveredMenuKey] = useState<string | null>(null);
  const [pendingDefaultReplacement, setPendingDefaultReplacement] =
    useState<DefaultReplacement | null>(null);
  const hasAppliedInitialDefault = useRef(false);
  const hasAttemptedInitialTeachingBubble = useRef(false);
  const hasTrackedTeachingBubble = useRef(false);
  const [isTeachingBubbleVisible, setIsTeachingBubbleVisible] = useState(false);
  const defaultTenantTeachingBubble = useMemo(
    () =>
      legacyNotifications.find((notification) =>
        notification.tag
          ?.toLowerCase()
          .includes(DEFAULT_TENANT_TEACHING_BUBBLE_TAG.toLowerCase())
      ),
    [legacyNotifications]
  );
  useEffect(() => {
    if (!hasAttemptedInitialTeachingBubble.current && defaultTenantTeachingBubble) {
      hasAttemptedInitialTeachingBubble.current = true;
      setIsTeachingBubbleVisible(true);
    }
  }, [defaultTenantTeachingBubble]);
  const closeTeachingBubble = useCallback(() => {
    hasAttemptedInitialTeachingBubble.current = true;
    setIsTeachingBubbleVisible(false);
  }, []);
  const onTeachingBubbleShown = useCallback(() => {
    if (hasTrackedTeachingBubble.current) {
      return;
    }

    hasTrackedTeachingBubble.current = true;
    trackDefaultTenantEvent('TeachingBubbleShown', {
      tenantId: ALL_DEFAULT_MENU_KEY,
      defaultTenantId,
      hasDefaultTenant: Boolean(defaultTenantId),
    });
  }, [defaultTenantId]);
  useEffect(() => {
    setDefaultTenantId(userDefaultTenantId);
  }, [userDefaultTenantId]);
  const onOverviewClick = useCallback(() => {
    closeTeachingBubble();
    handleManageViewClick(false);
    // Clear the selected tenant ID when navigating to "All" overview
    try {
      RtkStore.dispatch(setManageViewSelectedTenantId(null));
    } catch (error) {
      console.warn('Error clearing tenant selection:', error);
    }
    navigate.push(UATConstants.Routes.Overview);
  }, [closeTeachingBubble, handleManageViewClick, navigate]);
  const isMobile = useIsMobile(Viewport.MEDIUM);
  let tenantList = useSelector(selectTenants);
  const isTenantListLoaded = useSelector((state: StoreState) => Array.isArray(state.vacTenant));

  const isGivenTenantIdSelected = useCallback((tenantId: string): boolean => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlTenantId = urlParams.get('tenantId');

    if (!isManageViewsOpen && window.location.pathname === UATConstants.Routes.TenantOverviewHub) {
      return urlTenantId === tenantId;
    } else {
      return false;
    }
  }, [isManageViewsOpen]);

  const navigateToHubOverView = useCallback((tenantId: string) => {
    closeTeachingBubble();
    handleManageViewClick(false);
    // Store the selected tenant ID in Redux for ManageView to use later
    try {
      RtkStore.dispatch(setManageViewSelectedTenantId(tenantId));
    } catch (error) {
      console.warn('Error setting tenant selection:', error);
    }
    navigate.push(`${UATConstants.Routes.TenantOverviewHub}?tenantId=${tenantId}`);
  }, [closeTeachingBubble, handleManageViewClick, navigate]);

  const setDefaultMenu = useCallback(async (
    tenantId: string | null,
    menuName: string,
    action: DefaultTenantAction
  ) => {
    const isRemovingDefault = action === 'Remove';
    const previousDefaultTenantId = defaultTenantId;
    const defaultTenantIdToSave = isRemovingDefault
      ? null
      : tenantId ?? ALL_DEFAULT_MENU_KEY;
    const apiTenantId = defaultTenantIdToSave ?? 'null';
    let optimisticUserContextUpdate: Promise<void> | null = null;
    let optimisticUpdateError: unknown = null;

    try {
      setIsDefaultTenantSubmitting(true);
      setDefaultTenantId(defaultTenantIdToSave);
      optimisticUserContextUpdate = updateDefaultTenant(defaultTenantIdToSave).catch((error) => {
        console.error('Failed to update the cached default tenant:', error);
        optimisticUpdateError = error;
      });

      await ServiceFactoryClientExecuteApi.ExecuteRequestAsync(
        `${UATConstants.ApiPaths.MarkTenantAsDefault}/${encodeURIComponent(apiTenantId)}`,
        [],
        HttpVerbs.put,
        undefined
      );
      await optimisticUserContextUpdate;

      trackDefaultTenantEvent(`${action}-Success`, {
        action,
        tenantId: isRemovingDefault ? previousDefaultTenantId : defaultTenantIdToSave,
        previousDefaultTenantId,
        defaultTenantId: defaultTenantIdToSave,
        cacheUpdated: !optimisticUpdateError,
      });

      if (optimisticUpdateError) {
        showErrorToast('The default tenant was updated, but the local cache could not be saved', {
          scope: DEFAULT_TENANT_TOAST_SCOPE,
        });
      } else {
        showSuccessToast(isRemovingDefault
          ? `You've removed ${menuName} as your default tenant.`
          : `You've set ${menuName} as your default tenant.`, {
          scope: DEFAULT_TENANT_TOAST_SCOPE,
          ttlMs: 4000,
          emphasizedText: menuName,
        });
      }

      if (isRemovingDefault) {
        return;
      }

      handleManageViewClick(false);
      RtkStore.dispatch(setManageViewSelectedTenantId(tenantId));
      const defaultRoute = tenantId
        ? `${UATConstants.Routes.TenantOverviewHub}?tenantId=${tenantId}`
        : UATConstants.Routes.Overview;
      navigate.push(defaultRoute);
    } catch (error) {
      console.error('Failed to set the default tenant:', error);
      await optimisticUserContextUpdate;
      setDefaultTenantId(previousDefaultTenantId);
      try {
        await updateDefaultTenant(previousDefaultTenantId);
      } catch (rollbackError) {
        console.error('Failed to rollback the cached default tenant:', rollbackError);
      }
      showErrorToast(isRemovingDefault
        ? `Unable to remove '${menuName}' as your default tenant`
        : `Unable to set '${menuName}' as your default tenant`, {
        scope: DEFAULT_TENANT_TOAST_SCOPE,
      });
    } finally {
      setIsDefaultTenantSubmitting(false);
    }
  }, [defaultTenantId, handleManageViewClick, navigate, updateDefaultTenant]);

  const renderDefaultMenu = useCallback(
    (tenantId: string | null, menuName: string) => {
      const menuKey = tenantId ?? ALL_DEFAULT_MENU_KEY;
      const isDefault = tenantIdsMatch(defaultTenantId, menuKey);
      const canRemoveDefault = isDefault;
      const currentDefaultTenant = tenantList.find((tenant) =>
        tenantIdsMatch(tenant.TenantID, defaultTenantId)
      );
      const hasSelectedDefaultTenant =
        tenantIdsMatch(defaultTenantId, ALL_DEFAULT_MENU_KEY) || Boolean(currentDefaultTenant);
      const currentDefaultMenuName =
        tenantIdsMatch(defaultTenantId, ALL_DEFAULT_MENU_KEY)
          ? 'All'
          : currentDefaultTenant?.Tenant ?? '';
      const menuProps: IContextualMenuProps = {
        items: [
          {
            key: canRemoveDefault ? 'remove-default' : 'set-as-default',
            text: canRemoveDefault ? 'Remove default' : 'Set as default',
            iconProps: { iconName: canRemoveDefault ? 'Unpin' : 'Pinned' },
            disabled: isDefaultTenantSubmitting,
            onClick: () => {
              const action: DefaultTenantAction = canRemoveDefault
                ? 'Remove'
                : hasSelectedDefaultTenant ? 'Replace' : 'Set';
              trackDefaultTenantEvent('ActionSelected', {
                action,
                tenantId: menuKey,
                defaultTenantId,
                requiresConfirmation: action === 'Replace',
              });
              if (canRemoveDefault) {
                setDefaultMenu(null, menuName, 'Remove');
              } else if (hasSelectedDefaultTenant) {
                setPendingDefaultReplacement({
                  tenantId,
                  menuName,
                  currentMenuName: currentDefaultMenuName,
                });
              } else {
                setDefaultMenu(tenantId, menuName, 'Set');
              }
            },
          },
        ],
      };

      return (
        <DefaultMenuButton
          isDefault={isDefault}
          isHovered={hoveredMenuKey === menuKey}
          menuName={menuName}
          menuProps={menuProps}
          onMenuOpen={() => trackDefaultTenantEvent('MenuOpened', {
            tenantId: menuKey,
            defaultTenantId,
            isDefault,
          })}
          isTeachingBubbleVisible={menuKey === ALL_DEFAULT_MENU_KEY && isTeachingBubbleVisible}
          onTeachingBubbleClose={closeTeachingBubble}
          onTeachingBubbleShown={onTeachingBubbleShown}
          teachingBubble={defaultTenantTeachingBubble}
          isTeachingBubbleDismissSubmitting={isTeachingBubbleDismissSubmitting}
          onTeachingBubbleDismiss={(notificationId) =>
            dismissTeachingBubble([notificationId])
          }
        />
      );
    },
    [
      defaultTenantId,
      defaultTenantTeachingBubble,
      hoveredMenuKey,
      isTeachingBubbleDismissSubmitting,
      isDefaultTenantSubmitting,
      isTeachingBubbleVisible,
      closeTeachingBubble,
      onTeachingBubbleShown,
      dismissTeachingBubble,
      setDefaultMenu,
      tenantList,
    ]
  );

  const confirmDefaultReplacement = useCallback(() => {
    if (!pendingDefaultReplacement) {
      return;
    }

    const { tenantId, menuName } = pendingDefaultReplacement;
    setPendingDefaultReplacement(null);
    setDefaultMenu(tenantId, menuName, 'Replace');
  }, [pendingDefaultReplacement, setDefaultMenu]);

  const cancelDefaultReplacement = useCallback((dismissMethod: 'Close' | 'Cancel') => {
    if (!pendingDefaultReplacement) {
      return;
    }

    trackDefaultTenantEvent('ReplacementCancelled', {
      tenantId: pendingDefaultReplacement.tenantId ?? ALL_DEFAULT_MENU_KEY,
      defaultTenantId,
      dismissMethod,
    });
    setPendingDefaultReplacement(null);
  }, [defaultTenantId, pendingDefaultReplacement]);

  const onNewManageViewClick = useCallback(() => {
    closeTeachingBubble();
    handleManageViewClick(false);

    // Preserve current tenant selection when navigating to ManageView
    const urlParams = new URLSearchParams(window.location.search);
    const urlTenantId = urlParams.get('tenantId');

    if (urlTenantId && window.location.pathname === UATConstants.Routes.TenantOverviewHub) {
      try {
        RtkStore.dispatch(setManageViewSelectedTenantId(urlTenantId));
      } catch (error) {
        console.warn('Error setting tenant selection for ManageView:', error);
      }
    }

    navigate.push(UATConstants.Routes.ManageView);
  }, [closeTeachingBubble, handleManageViewClick, navigate]);
  const groups: INavLinkGroup[] = [
    {
      key: 'group1',
      links: [
        {
          name: 'Home',
          icon: 'HomeIcon',
          key: 'Home',
          ariaLabel: 'Home',
          defaultIsExpanded: true,
          'data-id': 'automation_id_22230',
          className: 'leftNavHome',
          links: [
            {
              name: 'All',
              key: 'Action Overview',
              ariaLabel: 'Action Overview',
              'data-id': 'automation_id_22230',
              onClick: onOverviewClick,
              isSelected:
                !isManageViewsOpen &&
                (window.location.pathname === UATConstants.Routes.Overview ||
                  window.location.pathname.toLowerCase().replace(/\s/g, '') ===
                  UATConstants.Routes.Home ||
                  window.location.pathname === UATConstants.Routes.LandingPage),
            },
          ],
        },
        {
          name: 'Manage View',
          icon: 'ManageIcon',
          key: 'Manage View',
          ariaLabel: 'Manage View',
          'data-id': 'automation_id_22234',
          onClick: onNewManageViewClick,
          isSelected:
            !isManageViewsOpen && window.location.pathname === UATConstants.Routes.ManageView,
        },
      ],
    },
  ];
  const homeNavigationLink = groups[0].links[0];

  // Track tenant list before any filtering
  try {
    trackUserContextTenantAuth(
      'UAT - Auth Tracking - LeftNavigation BEFORE Filtering',
      user.authorization?.TenantAuthorizations,
      user.flights,
      userHasRoleScope(),
      user.displayName,
      user.email
    );
  } catch (err) {
    // fail for telemetry
  }

  // Add Artemis Tenants which the user is Authorized to access in the left navigation
  if (userHasRoleScope()) {
    tenantList = tenantList.filter((item) => {
      if (item.TenantID === UATConstants.TenantIds.MEA) {
        return user?.flights?.includes(Flights.MEABedrockAccess);
      }
      if (UATConstants.FlightedItems.includes(item.TenantID)) {
        return user?.flights?.includes(Flights.ArtemisGA);
      }
      return true;
    });

    homeNavigationLink.links = homeNavigationLink.links || [];
    tenantList
      .filter((t) => LocalTenantConfig[t.TenantID]?.TenantExperience)
      .forEach((tenantName) => {
        homeNavigationLink.links?.push({
          name: tenantName?.Tenant,
          key: tenantName?.Tenant,
          ariaLabel: tenantName?.Tenant,
          'data-id': 'automation_id_22230',
          onClick: () => {
            navigateToHubOverView(tenantName?.TenantID);
          },
          onMouseEnter: () => setHoveredMenuKey(tenantName?.TenantID),
          onMouseLeave: () => setHoveredMenuKey(null),
          onFocus: () => setHoveredMenuKey(tenantName?.TenantID),
          onBlur: () => setHoveredMenuKey(null),
          isSelected: isGivenTenantIdSelected(tenantName?.TenantID),
          onRenderSecondaryContent: () =>
            renderDefaultMenu(tenantName?.TenantID, tenantName?.Tenant),
        });
      });
  }

  const allNavigationLink = homeNavigationLink.links?.find((link) => link.name === 'All');
  if (allNavigationLink) {
    allNavigationLink.onMouseEnter = () => setHoveredMenuKey(ALL_DEFAULT_MENU_KEY);
    allNavigationLink.onMouseLeave = () => setHoveredMenuKey(null);
    allNavigationLink.onFocus = () => setHoveredMenuKey(ALL_DEFAULT_MENU_KEY);
    allNavigationLink.onBlur = () => setHoveredMenuKey(null);
    allNavigationLink.onRenderSecondaryContent = () => renderDefaultMenu(null, 'All');
  }

  const availableTenantIds = useMemo(
    () =>
      tenantList
        .filter((tenant) => LocalTenantConfig[tenant.TenantID]?.TenantExperience)
        .map((tenant) => tenant.TenantID),
    [tenantList]
  );

  useEffect(() => {
    if (hasAppliedInitialDefault.current) {
      return;
    }

    const hasInvalidDefaultTenant =
      defaultTenantId &&
      !tenantIdsMatch(defaultTenantId, ALL_DEFAULT_MENU_KEY) &&
      !availableTenantIds.some((tenantId) => tenantIdsMatch(tenantId, defaultTenantId));

    if (hasInvalidDefaultTenant && !isTenantListLoaded) {
      return;
    }

    hasAppliedInitialDefault.current = true;

    if (
      (tenantIdsMatch(defaultTenantId, ALL_DEFAULT_MENU_KEY) || hasInvalidDefaultTenant) &&
      isInitialOverviewRoute
    ) {
      RtkStore.dispatch(setManageViewSelectedTenantId(null));
      navigate.replace(UATConstants.Routes.Overview);
    } else if (defaultTenantId && isInitialOverviewRoute) {
      RtkStore.dispatch(setManageViewSelectedTenantId(defaultTenantId));
      navigate.replace(`${UATConstants.Routes.TenantOverviewHub}?tenantId=${defaultTenantId}`);
    }
  }, [
    availableTenantIds,
    defaultTenantId,
    isInitialOverviewRoute,
    isTenantListLoaded,
    navigate,
  ]);

  //sorting left hand side menu as per name in hub overview
  if (groups.length > 0 && groups[0].links.length > 0) {
    homeNavigationLink.links?.sort((a, b) => {
      const getTenantId = (link: INavLink): string | null =>
        tenantList.find((tenant) => tenant.Tenant === link.name)?.TenantID ?? null;
      const aTenantId = a.name === 'All' ? ALL_DEFAULT_MENU_KEY : getTenantId(a);
      const bTenantId = b.name === 'All' ? ALL_DEFAULT_MENU_KEY : getTenantId(b);

      if (defaultTenantId && tenantIdsMatch(aTenantId, defaultTenantId)) {
        return -1;
      } else if (defaultTenantId && tenantIdsMatch(bTenantId, defaultTenantId)) {
        return 1;
      } else if (a.name === 'All') {
        return -1;
      } else if (b.name === 'All') {
        return 1;
      } else {
        return (a.name ?? '') < (b.name ?? '') ? -1 : 1;
      }
    });
  }

  const styles = React.useMemo(() => ({
    root: {
      '& nav button:not(.default-menu-action):hover': {
        backgroundColor: '#ffffff',
      },
      ...(isMobile && {
        '& nav:first-child > div:first-child': {
          maxHeight: '320px',
          overflow: 'auto',
        },
      }),
    },
    navContainer: {
      backgroundColor: '#f0f0f0',
    },
  }), [isMobile]);

  return (
    <>
      <Navigation isNavCollapsed={isNavCollapsed} groups={groups} styles={styles} />
      <Dialog
        hidden={!pendingDefaultReplacement}
        onDismiss={() => cancelDefaultReplacement('Close')}
        dialogContentProps={{
          type: DialogType.close,
          title: 'Replace default tenant?',
          closeButtonAriaLabel: 'Close',
          subText: pendingDefaultReplacement
            ? `You're about to replace ${pendingDefaultReplacement.currentMenuName} with ${pendingDefaultReplacement.menuName} as your default tenant.`
            : '',
        }}
        modalProps={{
          isBlocking: true,
          styles: {
            main: {
              maxWidth: 425,
            },
          },
        }}>
        <DialogFooter>
          <PrimaryButton
            disabled={isDefaultTenantSubmitting}
            onClick={confirmDefaultReplacement}
            text='Replace default'
          />
          <DefaultButton
            onClick={() => cancelDefaultReplacement('Cancel')}
            text='Cancel'
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};


// Generated by GitHub Copilot | Updated on: 2026-08-18 | 3 lines removed | Removed dead MarketplaceAccess flight branch in tenant filter
// Generated by GitHub Copilot | Updated on: 2026-08-20 | 3 lines modified | Set solid left navigation background
// Generated by GitHub Copilot | Updated on: 2026-09-08 | 28 lines modified | Hid My Dashboard and preserved Home tenant navigation
// Generated by GitHub Copilot | Updated on: 2026-09-28 | 120 lines modified | Added persistent default Hub Overview menu
// Generated by GitHub Copilot | Updated on: 2026-09-28 | 10 lines modified | Reloaded selected default and hid menu caret
// Generated by GitHub Copilot | Updated on: 2026-09-28 | 8 lines modified | Waited for tenants and removed menu caret
// Generated by GitHub Copilot | Updated on: 2026-09-28 | 6 lines modified | Preserved ADO default during phased tenant loading
// Generated by GitHub Copilot | Updated on: 2026-09-28 | 12 lines modified | Preserved nav state and aligned selection marker
// Generated by GitHub Copilot | Updated on: 2026-09-28 | 9 lines modified | Added removable pinned default tenant
// Generated by GitHub Copilot | Updated on: 2026-09-28 | 18 lines modified | Added pinned state and hover-only menus
// Generated by GitHub Copilot | Updated on: 2026-09-28 | 22 lines modified | Fixed hover visibility with row state
// Generated by GitHub Copilot | Updated on: 2026-09-28 | 18 lines modified | Added default tenant success messages
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 20 lines modified | Routed success messages through RTK store
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 52 lines modified | Added notification-driven default teaching bubble
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 90 lines modified | Kept default menu open with teaching bubble
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 32 lines modified | Controlled menu and teaching bubble independently
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 4 lines modified | Preserved menu while bubble is active
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 22 lines modified | Pointed bubble at default menu item
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 2 lines modified | Corrected menu target element typing
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 24 lines modified | Resolved rendered default menu target
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 25 lines modified | Captured rendered default menu reference
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 28 lines modified | Styled compact teaching bubble footer
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 12 lines modified | Fixed adaptive teaching bubble layout
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 28 lines modified | Forced white Got It button states
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 37 lines modified | Overrode teaching bubble button tint
// Generated by GitHub Copilot | Updated on: 2026-09-30 | 24 lines modified | Migrated default messages to toast
// Generated by GitHub Copilot | Updated on: 2026-09-30 | 35 lines modified | Preserved tab when removing default
// Generated by GitHub Copilot | Updated on: 2026-09-30 | 75 lines modified | Confirmed replacement before changing default
// Generated by GitHub Copilot | Updated on: 2026-09-30 | 1 line modified | Aligned replacement name fallback
// Generated by GitHub Copilot | Updated on: 2026-10-01 | 18 lines modified | Scoped hover color to ellipsis
// Generated by GitHub Copilot | Updated on: 2026-10-01 | 3 lines modified | Made all navigation row hovers white
// Generated by GitHub Copilot | Updated on: 2026-10-01 | 5 lines modified | Showed ellipsis over pinned tenant
// Generated by GitHub Copilot | Updated on: 2026-10-01 | 2 lines modified | Restored normal ellipsis color
// Generated by GitHub Copilot | Updated on: 2026-10-01 | 60 lines modified | Kept pin beside hover ellipsis
// Generated by GitHub Copilot | Updated on: 2026-10-05 | 35 lines modified | Persisted default bubble dismissal
// Generated by GitHub Copilot | Updated on: 2026-10-05 | 8 lines modified | Closed menu after bubble dismissal
// Generated by GitHub Copilot | Updated on: 2026-10-05 | 12 lines modified | Closed default menu on API success
// Generated by GitHub Copilot | Updated on: 2026-10-05 | 95 lines modified | Persisted default tenant via API
// Generated by GitHub Copilot | Updated on: 2026-10-05 | 30 lines modified | Refreshed default from user context
// Generated by GitHub Copilot | Updated on: 2026-10-06 | 55 lines modified | Added optimistic default state
// Generated by GitHub Copilot | Updated on: 2026-10-06 | 10 lines modified | Simplified default tenant state
// Generated by GitHub Copilot | Updated on: 2026-10-06 | 16 lines modified | Deferred cache failure message
// Generated by GitHub Copilot | Updated on: 2026-10-06 | 35 lines modified | Validated existing default tenant
// Generated by GitHub Copilot | Updated on: 2026-10-06 | 3 lines modified | Kept open menu ellipsis visible
// Generated by GitHub Copilot | Updated on: 2026-10-06 | 2 lines modified | Expanded Home navigation by default
// Generated by GitHub Copilot | Updated on: 2026-10-06 | 13 lines modified | Fell back to All for invalid default tenant
// Generated by GitHub Copilot | Updated on: 2026-10-09 | 22 lines modified | Showed All teaching bubble on Overview load
// Generated by GitHub Copilot | Updated on: 2026-10-09 | 14 lines modified | Included Home and Landing overview routes
// Generated by GitHub Copilot | Updated on: 2026-10-09 | 18 lines modified | Targeted initial bubble to All ellipsis
// Generated by GitHub Copilot | Updated on: 2026-10-09 | 8 lines modified | Anchored initial bubble to ellipsis button
// Generated by GitHub Copilot | Updated on: 2026-10-09 | 2 lines modified | Matched default tenant confirmation copy
// Generated by GitHub Copilot | Updated on: 2026-10-13 | 110 lines modified | Tracked default tenant adoption and interactions
// Generated by GitHub Copilot | Updated on: 2026-10-13 | 47 lines modified | Showed default teaching help only on All load
// Generated by GitHub Copilot | Updated on: 2026-10-13 | 6 lines modified | Kept focus with newly selected tenant
// Generated by GitHub Copilot | Updated on: 2026-10-13 | 1 line modified | Labelled the existing teaching help close icon
// Generated by GitHub Copilot | Updated on: 2026-10-14 | 100 lines modified | Show load-only help across default redirects
// Generated by GitHub Copilot | Updated on: 2026-10-23 | Copilot: Reconcile proven ADO and GHE content changes.
// Generated by GitHub Copilot | Updated on: 2026-10-21 | 150 lines removed | Removed duplicate default menu callbacks
// Generated by GitHub Copilot | Updated on: 2026-10-21 | 150 lines restored | Reverted default menu fix as requested
// Copilot change tracking: 2026-11-01 migration: Retain frozen release reconciliation.
// Copilot change tracking: 2026-11-02 frontend: Infer tenant-selector results and type default-tenant validation input.
